<template>
  <div class="asset-map">
    <div class="controls">
      <div class="interval-control">
        <label for="interval">Update Interval (seconds):</label>
        <input
          id="interval"
          type="number"
          v-model="updateInterval"
          @change="updateIntervalChange"
          min="5"
        />
      </div>
    </div>
    <div class="map-container">
      <img :src="backgroundImage" alt="Background" class="background-image" />
      <!-- 디버깅을 위한 태그 수 표시 -->
    
      <div
        v-for="tag in displayedTags"
        :key="tag.tag_mac"
        class="tag-marker"
        :class="{ 'tag-moving': animatingTags.includes(tag.tag_mac) }"
        :style="{
          left: `${tag.x}px`,
          top: `${tag.y}px`,
        }"
      >
        {{ tag.devicename }}
      </div>
    </div>
    <div class="location-groups">
      <div v-for="group in locationGroups" 
           :key="group.location" 
           class="location-group">
        <h3 class="location-title">{{ group.location }}</h3>
        <div class="gateway-name">Gateway: {{ group.gatewayName }}</div>
        <ul class="tag-list">
          <li v-for="tag in group.tags" 
              :key="tag.tag_mac" 
              class="tag-item">
            {{ tag.devicename }}
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      locationGroups: [],
      updateInterval: 10,
      intervalId: null,
      changedTags: [],
      displayedTags: [],
      serverTimeOffset: 0,
      gateways: [],
      rssiThresholds: {},
      animatingTags: [],
    };
  },
  computed: {
    activeDisplayedTags() {
      return this.displayedTags;
    },
    backgroundImage() {
      const company = localStorage.getItem('company');
      if (company === 'KAVALAN') {
        return require('@/assets/AssetMap2.png');
      } else if (company === 'AmericanWineries') {
        return require('@/assets/AssetMap2.png');
      } else if (company === 'PNK') {
        return require('@/assets/AssetMap_PNK.png');
      } else {
        return require('@/assets/AssetMap2.png');
      }
    }
  },
  methods: {
    async fetchData() {
      const company = localStorage.getItem('company');
      try {
        // 게이트웨이 정보 가져오기
        const gwResponse = await axios.get('/api/assetMap2', { params: { company } });
        console.log('Gateway response:', gwResponse.data); // 디버깅용 로그
        
        const locations = gwResponse.data;
        
        // 각 위치에 대한 태그 정보 가져오기
        const groups = await Promise.all(
          locations.map(async (loc) => {
            try {
              const tagResponse = await axios.get('/api/assetMap2tag', {
                params: { 
                  company,
                  location: loc.devicelocation
                }
              });
              console.log('Tags for', loc.devicelocation, ':', tagResponse.data); // 디버깅용 로그
              
              return {
                location: loc.devicelocation || 'Unknown Location',
                gatewayName: loc.devicename || 'Unknown Gateway',
                tags: tagResponse.data || []
              };
            } catch (error) {
              console.error(`Error fetching tags for location ${loc.devicelocation}:`, error);
              return {
                location: loc.devicelocation || 'Unknown Location',
                gatewayName: loc.devicename || 'Unknown Gateway',
                tags: [],
                error: error.message
              };
            }
          })
        );

        this.locationGroups = groups;
      } catch (error) {
        console.error('Error fetching data:', error);
        this.locationGroups = [];
      }
    },

    startFetching() {
      this.fetchData();
      if (this.intervalId) {
        clearInterval(this.intervalId);
      }
      this.intervalId = setInterval(() => {
        this.fetchData();
      }, this.updateInterval * 1000);
    },

    updateIntervalChange() {
      this.startFetching();
    },

    animateTag(tagMac) {
      // 애니메이션 클래스 추가
      this.animatingTags = [...this.animatingTags, tagMac];
      
      // 애니메이션 완료 후 클래스 제거
      setTimeout(() => {
        this.animatingTags = this.animatingTags.filter(id => id !== tagMac);
      }, 2000); // 2초 후 애니메이션 클래스 제거
    }
  },
  created() {
    this.startFetching();
  },
  beforeUnmount() {
    if (this.intervalId) {
      clearInterval(this.intervalId);
    }
  }
};
</script>

<style scoped>
.asset-map {
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100vh;
  padding: 20px;
}

.controls {
  width: 100%;
  display: flex;
  justify-content: center;
  margin-bottom: 20px;
}

.interval-control {
  display: flex;
  align-items: center;
  gap: 10px;
}

.interval-control input {
  width: 60px;
  padding: 4px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.map-container {
  width: 100%;
  height: 50vh;
  margin-bottom: 20px;
}

.background-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.location-groups {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  justify-content: center;
  padding: 20px;
}

.location-group {
  background-color: #f5f5f5;
  border-radius: 8px;
  padding: 20px;
  min-width: 250px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

.location-title {
  font-size: 1.2em;
  font-weight: bold;
  margin-bottom: 10px;
  color: #333;
}

.gateway-name {
  color: #666;
  margin-bottom: 15px;
  font-size: 0.9em;
}

.tag-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.tag-item {
  padding: 8px;
  border-bottom: 1px solid #eee;
  color: #444;
}

.tag-item:last-child {
  border-bottom: none;
}

.tag-marker {
  position: absolute;
  transform: translate(-50%, -50%);
  padding: 8px 12px;
  border-radius: 4px;
  background-color: rgba(0, 238, 255, 0.8);
  color: black;
  font-size: 14px;
  font-weight: bold;
  white-space: nowrap;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0,0,0,0.2);
  border: 1px solid rgba(0,0,0,0.1);
  transition: all 0.5s ease; /* 기본 전환 효과 */
}

.tag-moving {
  background-color: rgba(255, 165, 0, 0.8); /* 이동 중일 때 오렌지색으로 변경 */
  transform: translate(-50%, -50%) scale(1.1);
  box-shadow: 0 4px 8px rgba(0,0,0,0.3);
  z-index: 1001;
}

.tag-marker:hover {
  background-color: rgba(0, 200, 255, 0.9);
  transform: translate(-50%, -50%) scale(1.1);
  z-index: 1002;
}

.debug-info {
  position: absolute;
  top: 10px;
  left: 10px;
  background-color: rgba(255, 255, 255, 0.8);
  padding: 5px;
  z-index: 1001;
}

.weak-signal {
  color: gray !important;
}
</style>

